import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import { fmtDate } from '../../../../utils/fmt';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';
import ConvertActionButton from '../../../Reusable_Components/ConvertActionButton';
import useDetailFetch from '../../../../hooks/useDetailFetch';

const STATUS_COLORS = {
  'New': '#3b82f6', 'Contacted': '#8b5cf6', 'Qualifying': '#f59e0b',
  'Qualified': '#22c55e', 'Proposal Sent': '#0ea5e9', 'Negotiating': '#e87860',
  'Converted': '#16a34a', 'Unqualified': '#94a3b8', 'Lost': '#ef4444',
};

const scoreColor = (v) => {
  const n = Number(v);
  if (n >= 80) return '#16a34a';
  if (n >= 60) return '#3b82f6';
  if (n >= 35) return '#f59e0b';
  return '#ef4444';
};


const fmtKES = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return '—';
  return 'KES ' + num.toLocaleString('en-KE');
};

const Badge = ({ value, color }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 12px', borderRadius: 99,
    background: (color || '#94a3b8') + '18',
    border: `1px solid ${(color || '#94a3b8')}40`,
    fontSize: 12, fontWeight: 600, color: color || '#94a3b8',
  }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: color || '#94a3b8' }} />
    {value || '—'}
  </span>
);

const Section = ({ icon, title, children }) => (
  <div className="card" style={{ marginBottom: 0 }}>
    <div className="card-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
        <div style={{ width: 32, height: 32, borderRadius: 7, background: 'var(--color-primary-pale)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{icon}</div>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{title}</span>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, value, wide }) => (
  <div className={`form-field${wide ? ' span-full' : ''}`}>
    <div className="form-label">{label}</div>
    <div style={{ fontSize: 14, color: value ? 'var(--text-main)' : 'var(--text-muted)', fontWeight: value ? 500 : 400, padding: '8px 12px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)', minHeight: 38, wordBreak: 'break-word' }}>
      {value || '—'}
    </div>
  </div>
);

const SingleLeadDetails = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const id = leadId ? atob(leadId).replace(/\D/g, '') : '';
  const { data: lead, loading } = useDetailFetch(id ? `${API_DOMAIN}${API_ROUTES.lead}${id}/` : null);
  const [showDelete, setShowDelete] = useState(false);
  const [linkedOpportunities, setLinkedOpportunities] = useState([]);

  useEffect(() => {
    axios.get(`${API_DOMAIN}${API_ROUTES.opportunity}`, { headers, params: { page_size: 500 } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || res.data?.data || []);
        setLinkedOpportunities(list.filter(o => String(o.related_lead) === String(id)));
      })
      .catch(() => {});
  }, [id]); // eslint-disable-line

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_DOMAIN}${API_ROUTES.lead}${id}/`, { headers });
      toast?.addToast({ type: 'success', message: 'Lead deleted.' });
      navigate('/lead/list');
    } catch {
      toast?.addToast({ type: 'error', message: 'Failed to delete lead.' });
    }
  };

  if (loading) return (
    <div className="card"><div className="card-body">
      {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 18, width: `${60 + i * 8}%`, borderRadius: 4, marginBottom: 12 }} />)}
    </div></div>
  );

  if (!lead) return (
    <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
      Lead not found. <Link to="/lead/list" style={{ color: 'var(--color-primary)' }}>Back to list</Link>
    </div></div>
  );

  const statusColor = STATUS_COLORS[lead.status];
  const score = Number(lead.lead_score);
  const contact = (typeof lead.contact === 'object' && lead.contact) || lead.contact_data || {};
  const contactName = contact.name || contact.first_name || lead.contact_name || '';

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
            <Link to="/lead/list" style={{ color: 'var(--color-primary)' }}>Leads</Link>
            <span>›</span>
            <span>{lead.title || contactName || `Lead #${lead.id}`}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>
              {lead.title || contactName || `Lead #${lead.id}`}
            </h1>
            <Badge value={lead.status} color={statusColor} />
            {lead.lead_score !== undefined && lead.lead_score !== null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: scoreColor(score) }}>
                Score: {score}
              </span>
            )}
          </div>
          {lead.priority && (
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
              Priority: <span style={{ fontWeight: 600, color: lead.priority === 'High' ? '#b91c1c' : lead.priority === 'Medium' ? '#a16207' : '#15803d' }}>{lead.priority}</span>
            </div>
          )}
        </div>
        <div className="page-actions">
          <button className="btn btn-danger" onClick={() => setShowDelete(true)}>Delete</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/lead/input/${btoa(lead.id)}`)}>Edit Lead</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/lead/history/list/${lead.id}`)}>History</button>
          {lead.status !== 'Converted' && (
            <ConvertActionButton
              label="Convert to Opportunity"
              url={`${API_DOMAIN}${API_ROUTES.lead}${lead.id}/convert-to-opportunity/`}
              body={{
                opportunity_name: lead.title || contactName || `Lead #${lead.id}`,
                stage: 'Qualification',
                value: lead.expected_value || 0,
                expected_close_date: lead.expected_close_date || new Date(Date.now() + 30 * 86400000).toISOString(),
                company: lead.company,
              }}
              buildNavigateTo={(opp) => `/opportunity/input/${btoa(opp.id)}`}
            />
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>

        {/* Left */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <Section icon="👤" title="Contact Information">
            <div className="form-grid">
              <Field label="Contact Name" value={contactName} />
              <Field label="Company" value={contact.company || lead.company} />
              <Field label="Email" value={contact.email || lead.email} />
              <Field label="Phone" value={contact.phone || lead.phone} />
            </div>
          </Section>

          <Section icon="📊" title="Pipeline Details">
            <div className="form-grid">
              <Field label="Lead Source" value={lead.lead_source} />
              <Field label="Expected Value" value={fmtKES(lead.expected_value)} />
              <Field label="Expected Close Date" value={fmtDate(lead.expected_close_date)} />
              <Field label="Follow-up Date" value={fmtDate(lead.follow_up_date)} />
            </div>
          </Section>

          {linkedOpportunities.length > 0 && (
            <Section icon="🔗" title="Pipeline">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {linkedOpportunities.map(o => (
                  <button
                    key={o.id}
                    className="btn btn-ghost"
                    style={{ justifyContent: 'flex-start', gap: 8 }}
                    onClick={() => navigate(`/opportunity/details/${btoa(o.id)}`)}
                  >
                    💼 {o.opportunity_name} ({o.stage})
                  </button>
                ))}
              </div>
            </Section>
          )}

          <Section icon="👥" title="Assignment">
            <div className="form-grid">
              <Field label="Assigned To" value={lead.assigned_to_names || (Array.isArray(lead.assigned_to) ? lead.assigned_to.join(', ') : lead.assigned_to)} />
              <Field label="Created" value={fmtDate(lead.created_at)} />
            </div>
          </Section>

          {(lead.description) && (
            <Section icon="📝" title="Notes">
              <div style={{ fontSize: 14, color: 'var(--text-sub)', lineHeight: 1.6, padding: '2px 0' }}>
                {lead.description}
              </div>
            </Section>
          )}
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Score card */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Lead Score</span>
              <span style={{ fontSize: 24, fontWeight: 800, color: scoreColor(score) }}>{isNaN(score) ? '—' : score}</span>
            </div>
            <div className="card-body">
              <div style={{ height: 8, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 99, width: `${Math.min(score, 100)}%`, background: scoreColor(score), transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: 11, color: scoreColor(score), fontWeight: 600, marginTop: 8, textAlign: 'center' }}>
                {score >= 80 ? 'Hot 🔥' : score >= 60 ? 'Warm' : score >= 35 ? 'Nurturing' : 'Cold'}
              </div>
            </div>
          </div>

          {/* Key info */}
          <div className="card">
            <div className="card-header"><span className="card-title">Key Info</span></div>
            <div className="card-body">
              {[
                { label: 'Status',   value: lead.status   || '—' },
                { label: 'Priority', value: lead.priority  || '—' },
                { label: 'Source',   value: lead.lead_source || '—' },
                { label: 'Created',  value: fmtDate(lead.created_at) },
                { label: 'Updated',  value: fmtDate(lead.updated_at) },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                  <span style={{ color: 'var(--text-main)', fontWeight: 500, textAlign: 'right' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/lead/list')}>
            ← Back to Leads
          </button>
        </div>
      </div>

      <ConfirmModal
        open={showDelete}
        title="Delete Lead"
        message="Are you sure you want to permanently delete this lead?"
        confirmLabel="Delete"
        confirmDanger
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
};

export default SingleLeadDetails;
